/**
 * 422 de POST /customer/leads/:lead/insured (gateway → ms-agl) cuando el CAP de la
 * dirección de residencia no es del comune elegido:
 * `{"error":"INVALID_RESIDENTIAL_ZIP_CITY","field":"residentialAddress.zipCode",
 * "zipCode":"20004","city":"15010","validZipRanges":[{"from":"20010","to":"20010"}]}`.
 *
 * Antes no se comprobaba al guardar y AGL rechazaba la propuesta al aceptar la nota
 * legal («Invalid residential address city or zip code»): el cliente se enteraba al
 * final del funnel (Arluno con 20004, 03-nov-2026). Ahora el error se pinta en el campo
 * del CAP de /coverage/1 con el CAP (o los CAP) válidos del comune.
 *
 * HTTPService (parseError) deja el `error` del cuerpo en `code` y `validZipRanges` tal
 * cual; `message` es el texto técnico de ms-agl y no se enseña.
 */
export const INVALID_RESIDENTIAL_ZIP_CITY = "INVALID_RESIDENTIAL_ZIP_CITY";

export interface ZipRangeI {
  from: string;
  to: string;
}

export interface ResidentialZipCityRejectionI {
  /** Tramos de CAP del comune según el catálogo de ms-agl (vacío si no constan). */
  validZipRanges: ZipRangeI[];
}

const isZipRange = (value: unknown): value is ZipRangeI => {
  const range = value as { from?: unknown; to?: unknown } | null;
  return (
    null !== range &&
    "object" === typeof range &&
    "string" === typeof range.from &&
    "string" === typeof range.to
  );
};

export const residentialZipCityRejection = (
  error: unknown
): ResidentialZipCityRejectionI | null => {
  const { status, code, message, validZipRanges } = (error ?? {}) as {
    status?: unknown;
    code?: unknown;
    message?: unknown;
    validZipRanges?: unknown;
  };
  if (422 !== Number(status)) return null;
  if (INVALID_RESIDENTIAL_ZIP_CITY !== code && INVALID_RESIDENTIAL_ZIP_CITY !== message) {
    return null;
  }
  return {
    validZipRanges: Array.isArray(validZipRanges) ? validZipRanges.filter(isZipRange) : [],
  };
};

type TranslateFn = (key: string) => string;

const translateOr = (translate: TranslateFn, key: string, fallback: string): string => {
  const value = translate(key);
  return value === key || !value ? fallback : value;
};

// Claves pendientes en getlife/translations (docs/i18n-pending.md). Solo native IT.
export const RESIDENTIAL_ZIP_CITY_FALLBACKS = {
  mismatch: "Il CAP {zip} non corrisponde al comune selezionato.",
  validOne: "Per {city} il CAP corretto è {valid}.",
  validMany: "Per {city} i CAP validi sono {valid}.",
  thisCity: "questo comune",
  range: "da {from} a {to}",
  check: "Controlla il CAP e il comune.",
};

const KEYS = {
  mismatch: "postPayment.address.zipCity.mismatch",
  validOne: "postPayment.address.zipCity.validOne",
  validMany: "postPayment.address.zipCity.validMany",
  thisCity: "postPayment.address.zipCity.thisCity",
  range: "postPayment.address.zipCity.range",
  check: "postPayment.address.zipCity.check",
} as const;

const fill = (template: string, values: Record<string, string>): string =>
  Object.entries(values).reduce((text, [key, value]) => text.split(`{${key}}`).join(value), template);

/**
 * Texto para el campo del CAP: «Il CAP 20004 non corrisponde al comune selezionato.
 * Per Arluno il CAP corretto è 20010.» Con un tramo («da 20100 a 20199») o varios CAP,
 * la variante plural; sin CAP válidos conocidos, «Controlla il CAP e il comune.».
 */
export const residentialZipCityMessage = (
  translate: TranslateFn,
  rejection: ResidentialZipCityRejectionI,
  zipCode: string,
  cityName: string | null
): string => {
  const t = (key: keyof typeof KEYS) =>
    translateOr(translate, KEYS[key], RESIDENTIAL_ZIP_CITY_FALLBACKS[key]);
  const first = fill(t("mismatch"), { zip: zipCode.trim() });
  const ranges = rejection.validZipRanges;
  if (0 === ranges.length) return `${first} ${t("check")}`;

  const valid = ranges
    .map((r) => (r.from === r.to ? r.from : fill(t("range"), { from: r.from, to: r.to })))
    .join(", ");
  const single = 1 === ranges.length && ranges[0].from === ranges[0].to;
  const city = cityName && "" !== cityName.trim() ? cityName.trim() : t("thisCity");
  return `${first} ${fill(t(single ? "validOne" : "validMany"), { city, valid })}`;
};
